import { useEffect, useId, useLayoutEffect, useRef, type CSSProperties, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BannerShield } from './BannerShield';
import { ScanResults } from './ScanResults';
import { useScan } from './useScan';
import type { ScanErrorCode } from '@/lib/vscanner/types';
import styles from './VScannerBanner.module.css';

// Artwork over the red gradient (--vn-banner-bg), which stays as the fallback layer.
const BANNER_ART: CSSProperties = {
  background: 'url(/assets/vscanner-bg.png) center / cover no-repeat, var(--vn-banner-bg)',
};

// Banner width the design was drawn for (860px column less its 40px margin), and the floor
// so a narrow panel does not shrink the copy past reading.
const DESIGN_WIDTH = 820;
const MIN_SCALE = 0.4;

const LOGIN_PATH = '/auth/login';
const REGISTER_PATH = '/auth/register';

const ERROR_COPY: Record<ScanErrorCode, readonly [string, string]> = {
  TIMEOUT: [
    'Scan timed out',
    'The website took longer than 90 seconds to respond. This can happen behind strict bot protection. Try again, or try the www variant.',
  ],
  UNREACHABLE: [
    'Could not reach the website',
    'The domain resolves, but the server did not respond on port 80/443. Check that the site is up and publicly reachable.',
  ],
  RATE_LIMIT: ['The scanner is busy', 'The scanner is overloaded. Please try again in a minute.'],
  UNKNOWN: ['Something went wrong', 'The scan failed unexpectedly. Please try again.'],
};

const IDLE_LEAD =
  'Enter your domain to get a website security report based on the OWASP Top 10 vulnerability list, plus security headers, SSL/TLS and more.';

const LOGIN_NOTICE = 'Sign in to see the full report';

function focusLoginEmail() {
  const input = document.querySelector<HTMLInputElement>('input[type="email"]');
  if (!input) return;
  const rect = input.getBoundingClientRect();
  if (rect.top < 0 || rect.bottom > window.innerHeight) {
    input.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }
  input.focus({ preventScroll: true });
}

/**
 * VScanner banner for the sign in / sign up right-hand panel. Scan state lives in the
 * `useScan` store, so it survives switching between the two pages.
 */
export function VScannerBanner() {
  const scan = useScan();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const prevPhase = useRef(scan.phase);
  const bannerRef = useRef<HTMLDivElement>(null);
  const shieldRef = useRef<HTMLDivElement>(null);
  const inputId = useId();
  const errorId = useId();

  const arrivedToLogin = (location.state as { scanFocus?: boolean } | null)?.scanFocus === true;
  useEffect(() => {
    if (!arrivedToLogin) return;
    const id = window.setTimeout(focusLoginEmail, 60);
    return () => window.clearTimeout(id);
  }, [arrivedToLogin]);

  // Scale the content to the banner width (declared before the shield fit below so that one
  // measures the scaled layout).
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;
    const apply = () =>
      banner.style.setProperty('--vs', String(Math.min(1, Math.max(MIN_SCALE, banner.clientWidth / DESIGN_WIDTH))));
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(banner);
    return () => observer.disconnect();
  }, []);

  // Size the ring stack from where the idle copy ends, so the outer ring never crosses text.
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    const shield = shieldRef.current;
    if (!banner || !shield || scan.phase !== 'idle') return;
    const fit = () => {
      const top = banner.getBoundingClientRect().top;
      let bottom = 0;
      banner.querySelectorAll('h2, p, input').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.height) bottom = Math.max(bottom, r.bottom - top);
      });
      const gap = 28 * (Number(banner.style.getPropertyValue('--vs')) || 1);
      if (bottom) shield.style.setProperty('--apex', `${Math.round(bottom + gap)}px`);
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(banner);
    void document.fonts?.ready.then(fit);
    return () => observer.disconnect();
  }, [scan.phase, scan.urlError]);

  useEffect(() => {
    const prev = prevPhase.current;
    prevPhase.current = scan.phase;
    if (scan.phase === prev) return;
    if (scan.phase === 'scanning') cancelRef.current?.focus();
    else if (scan.phase === 'idle' && prev !== 'idle') inputRef.current?.focus();
  }, [scan.phase]);

  const hostQuery = scan.host ? `?host=${encodeURIComponent(scan.host)}` : '';
  const goLogin = () => {
    // The login form shows `notice` as an info line above the fields.
    const state = { ...(location.state as object | null), scanFocus: true, notice: LOGIN_NOTICE };
    if (location.pathname === LOGIN_PATH) {
      navigate({ pathname: LOGIN_PATH, search: location.search }, { replace: true, state });
      focusLoginEmail();
    } else {
      navigate({ pathname: LOGIN_PATH, search: hostQuery }, { state });
    }
  };
  const goSignup = () => navigate({ pathname: REGISTER_PATH, search: hostQuery });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    scan.start();
  };

  const useExample = () => {
    if (!scan.urlError) return;
    scan.setUrl(scan.urlError.example);
    inputRef.current?.focus();
  };

  const { phase } = scan;
  const result = phase === 'results' ? scan.result : null;
  const eyebrow = phase === 'results' ? 'Security check results:' : 'VScanner - Website Security Check';
  const [errorTitle, errorBody] = ERROR_COPY[scan.errorCode ?? 'TIMEOUT'];
  const title =
    phase === 'scanning'
      ? `Checking ${scan.host}`
      : phase === 'error'
        ? 'Scan not completed'
        : phase === 'results'
          ? scan.host
          : 'Check your website security';
  const lead =
    phase === 'scanning'
      ? 'This check only reads publicly available data and doesn’t change anything on your website.'
      : phase === 'error'
        ? `No results were recorded for ${scan.host}.`
        : null;
  const progress = Math.max(0, Math.min(100, Math.round(scan.percent)));
  const recentTasks = scan.tasks.length ? scan.tasks.slice(-5) : ['Initializing scanner'];

  return (
    <div ref={bannerRef} className={styles.banner} style={BANNER_ART} data-vscanner data-phase={phase}>
      <div ref={shieldRef} className={styles.shield} aria-hidden="true">
        <BannerShield />
      </div>
      <div className={styles.inner}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <h2 className={styles.title}>{title}</h2>

        {phase === 'idle' ? (
          <>
            <form className={`${styles.field} ${scan.urlError ? styles.fieldInvalid : ''}`} onSubmit={onSubmit} noValidate>
              <label className={styles.srOnly} htmlFor={inputId}>
                Website to check
              </label>
              <input
                ref={inputRef}
                id={inputId}
                className={styles.input}
                type="text"
                inputMode="url"
                autoComplete="url"
                spellCheck={false}
                placeholder="Domain, e.g. example.com"
                value={scan.url}
                aria-invalid={scan.urlError ? true : undefined}
                aria-describedby={scan.urlError ? errorId : undefined}
                onChange={(e) => scan.setUrl(e.target.value)}
              />
              <button type="submit" className={styles.scanButton}>
                Scan now <span aria-hidden="true">→</span>
              </button>
            </form>
            {scan.urlError ? (
              <div id={errorId} className={styles.callout} role="alert">
                <svg className={styles.calloutIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v4.5M12 16h.01" />
                </svg>
                <div className={styles.calloutBody}>
                  <div className={styles.calloutTitle}>{scan.urlError.title}</div>
                  <div className={styles.calloutHint}>{scan.urlError.hint}</div>
                  <button type="button" className={styles.example} onClick={useExample}>
                    Try: <span>{scan.urlError.example}</span>
                  </button>
                </div>
              </div>
            ) : null}
            <p className={`${styles.lead} ${styles.leadIdle}`}>{IDLE_LEAD}</p>
          </>
        ) : null}

        {lead ? <p className={styles.lead}>{lead}</p> : null}

        {phase === 'scanning' ? (
          <div className={`${styles.card} ${styles.cardScan}`} data-theme="light" role="status" aria-live="polite">
            <div className={styles.scanHead}>
              <div className={styles.scanHeadText}>
                <div className={styles.scanLabel}>Scanning</div>
                <div className={styles.scanHost}>{scan.host}</div>
              </div>
              <button ref={cancelRef} type="button" className={styles.secondaryButton} onClick={scan.cancel}>
                Cancel
              </button>
            </div>
            <div className={styles.progressRow}>
              <div
                className={styles.progress}
                role="progressbar"
                aria-label="Scan progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progress}
              >
                <div className={styles.progressFill} style={{ width: `${progress}%` }} />
              </div>
              <span className={styles.progressPct}>{progress}%</span>
            </div>
            <ol className={styles.steps}>
              {recentTasks.map((label, i) => {
                const active = i === recentTasks.length - 1;
                return (
                  <li
                    key={label}
                    className={`${styles.step} ${active ? '' : styles.stepDone}`}
                    aria-current={active ? 'step' : undefined}
                  >
                    <span className={`${styles.marker} ${active ? '' : styles.markerDone}`} aria-hidden="true">
                      {active ? (
                        <span className={styles.spinner} />
                      ) : (
                        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 10.5 8 14.5 16 6" />
                        </svg>
                      )}
                    </span>
                    {label}
                  </li>
                );
              })}
            </ol>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.card} data-theme="light" role="alert">
            <div className={styles.errorRow}>
              <span className={styles.errorTile} aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                </svg>
              </span>
              <div className={styles.errorText}>
                <div className={styles.errorTitle}>{errorTitle}</div>
                <div className={styles.errorBody}>{errorBody}</div>
              </div>
            </div>
            <div className={styles.actions}>
              {scan.errorCode !== 'RATE_LIMIT' ? (
                <button type="button" className={styles.retryButton} onClick={scan.retry}>
                  Try again
                </button>
              ) : null}
              <button type="button" className={styles.secondaryButton} onClick={scan.reset}>
                Scan another website
              </button>
            </div>
          </div>
        ) : null}

        {result ? <ScanResults result={result} onLogin={goLogin} onSignup={goSignup} /> : null}
      </div>
    </div>
  );
}

export default VScannerBanner;
